import React from "react";
import Link from "next/link";
import { PlanningContextProvider } from "../components/planning/state/PlanningContext";
import { PlanningSearchProvider } from "../components/planning/state/PlanningSearchContext";
import PlanningDesignSwitcher from "../components/planning/layout/PlanningDesignSwitcher";
import Meta from "../components/Meta";
import {
  fetchApplicationsLight,
  fetchPlanningFeedLightPage,
  fetchAllApplicationsLight,
  fetchAllUnifiedPlanningFeed,
} from "../lib/services/applicationsApiService";
import {
  buildMonthlyCounts,
  extractPostcodeDistrict,
  filterShortLetsPastYear,
  isRetrospectiveShortLet,
  topCounts,
} from "../lib/shortLets/analytics";
import { SITE_URL, toAbsoluteSiteUrl } from "../lib/siteUrl";
import {
  buildPlanningApplicationPath,
} from "../lib/planningApplicationUrl";
import { buildPlanningArchivePath } from "../lib/planningArchive";
import {
  AGGREGATE_COUNCIL_SLUG,
  COUNCILS,
  DEFAULT_COUNCIL_SLUG,
  applicationIdentityKey,
  councilSlugToShortCode,
  getCouncilQualifiedApplicationReference,
  getCouncilConfig,
  isAggregateCouncilScope,
} from "../lib/councils";
import { isLondonScope } from '../lib/services/londonPlanningService';
import { getCityScopeBackendBaseUrl } from "../lib/backendStats";
import {
  PLANNING_COVERAGE_LINKS,
  PLANNING_CURRENT_COVERAGE_COPY,
  getPlanningSeoAuthorityName,
  getPlanningSeoDescription,
  getPlanningSeoPlaceName,
} from "../lib/planningSeo";
import CouncilSearchGuide, {
  ScottishCouncilCoverageDirectory,
} from "../components/planning/CouncilSearchGuide";
import { getScottishCouncilSeoProfile } from "../lib/scottishCouncilSeo";
import { INTERACTIVE_FEED_PAGE_SIZE } from "../lib/planning/feedPerformance";

const TEXT_INDEX_LIMIT = 90;
const ADDRESS_INDEX_LIMIT = 40;
const INITIAL_PAGE_FETCH_LIMIT = 500;
const INITIAL_PAGE_FETCH_PAGE_SIZE = 100;
const INITIAL_PAGE_FETCH_MAX_PAGES = 10;
// Keep enough recent links in the server-rendered document for discovery while
// avoiding a duplicated 100-record JSON payload. The interactive read model
// supplies the authoritative first page and every subsequent cursor page.
const INTERACTIVE_INITIAL_PAGE_FETCH_LIMIT = 30;
const INTERACTIVE_INITIAL_PAGE_FETCH_PAGE_SIZE = 30;
const INTERACTIVE_INITIAL_PAGE_FETCH_MAX_PAGES = 1;
const COUNCIL_COUNT_TIMEOUT_MS = 12000;
const IS_PRODUCTION_BUILD = process.env.NEXT_PHASE === "phase-production-build";
const PRODUCTION_BUILD_FEED_TIMEOUT_MS = 4000;
const PLANNING_REGION_ROUTE_EVENT = "cityscope:planning-region-route";
const FETCH_STATIC_COUNCIL_COUNTS =
  process.env.CITYSCOPE_FETCH_STATIC_COUNTS === "1";
const FALLBACK_COUNCIL_APPLICATION_COUNTS = {
  edinburgh: 6053,
  midlothian: 873,
  'east-lothian': 1223,
  'west-lothian': 831,
  falkirk: 620,
  clackmannanshire: 242,
};

function getPlanningUpdatesScope(councilSlug = AGGREGATE_COUNCIL_SLUG) {
  if (councilSlug === 'london') return {
    aggregate: false, council: null, slug: 'london', apiCouncil: 'london',
    placeName: 'London', authorityName: 'London planning authorities',
  };
  const aggregate = isAggregateCouncilScope(councilSlug);
  const council = aggregate
    ? null
    : getCouncilConfig(councilSlug) || getCouncilConfig(DEFAULT_COUNCIL_SLUG);

  return {
    aggregate,
    council,
    slug: aggregate ? AGGREGATE_COUNCIL_SLUG : council?.slug || DEFAULT_COUNCIL_SLUG,
    apiCouncil: aggregate ? AGGREGATE_COUNCIL_SLUG : council?.slug || DEFAULT_COUNCIL_SLUG,
    placeName: getPlanningSeoPlaceName(aggregate ? AGGREGATE_COUNCIL_SLUG : council?.slug || DEFAULT_COUNCIL_SLUG),
    authorityName: getPlanningSeoAuthorityName(aggregate ? AGGREGATE_COUNCIL_SLUG : council?.slug || DEFAULT_COUNCIL_SLUG),
  };
}

function getPlanningUpdatesScopeSlugFromPath(pathname = "", fallbackSlug = AGGREGATE_COUNCIL_SLUG) {
  const cleanPath = String(pathname || "").split("?")[0].split("#")[0];
  if (cleanPath === "/planning-updates") return AGGREGATE_COUNCIL_SLUG;
  if (!cleanPath.startsWith("/planning-updates/")) return fallbackSlug;

  const routeCouncil = cleanPath.split("/").filter(Boolean)[1];
  return routeCouncil === 'london' ? 'london' : getCouncilConfig(routeCouncil)?.slug || fallbackSlug;
}

function getPlanningUpdatesAreaIdFromPath(pathname = "") {
  const cleanPath = String(pathname || "").split("?")[0].split("#")[0];
  if (!cleanPath.startsWith("/planning-updates/")) return null;
  const parts = cleanPath.split("/").filter(Boolean);
  const council = getCouncilConfig(parts[1]);
  const areaSlug = parts[2];
  return council && areaSlug ? `${council.slug}:${String(areaSlug).trim().toLowerCase()}` : null;
}

function toDateLabel(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return date.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });
  } catch (_) {
    return date.toISOString().slice(0, 10);
  }
}

function getSortTimestamp(application) {
  const value =
    application?.created_at
    || application?.scraped_date
    || application?.validated_date
    || application?.received_date;
  const ts = value ? new Date(value).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
}

function getCouncilConfigFromApplication(application) {
  const rawCouncil =
    application?.source_council ||
    application?.council_slug ||
    application?.council ||
    application?.sourceCouncil;

  const rawCouncilConfig = getCouncilConfig(rawCouncil);
  if (rawCouncilConfig) return rawCouncilConfig;

  const sourceUrl = String(
    application?.link ||
    application?.url ||
    application?.application_url ||
    application?.source_url ||
    ""
  ).toLowerCase();

  if (!sourceUrl) return null;

  return Object.values(COUNCILS).find((council) => {
    const portalBase = String(council.portalBaseUrl || "").toLowerCase();
    if (!portalBase) return false;
    try {
      const portalHost = new URL(portalBase).hostname.toLowerCase();
      return sourceUrl.includes(portalHost);
    } catch (_) {
      return sourceUrl.includes(portalBase);
    }
  }) || null;
}

function getApplicationCouncilSlug(application, fallbackCouncilSlug = DEFAULT_COUNCIL_SLUG) {
  return getCouncilConfigFromApplication(application)?.slug || fallbackCouncilSlug;
}

function isPublicPlanningApplication(application) {
  const council = getCouncilConfigFromApplication(application);
  return council ? council.publicEnabled === true : true;
}

function filterPlanningApplicationsForScope(applications, scope) {
  const items = Array.isArray(applications) ? applications : [];
  if (scope?.slug === 'london') return items.filter((app) => isLondonScope(app.source_council));
  if (!scope?.aggregate) {
    return items.filter((application) => getApplicationCouncilSlug(application, scope?.slug) === scope?.slug);
  }

  return items.filter((app) => !isLondonScope(app.source_council) && isPublicPlanningApplication(app));
}

function buildTextIndex(applications) {
  const items = Array.isArray(applications) ? applications : [];
  const seen = new Set();

  const deduped = items.filter((app) => {
    const key = applicationIdentityKey(app || {});
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  deduped.sort((a, b) => getSortTimestamp(b) - getSortTimestamp(a));
  return deduped.slice(0, TEXT_INDEX_LIMIT);
}

function countUniqueApplications(applications) {
  if (!Array.isArray(applications) || applications.length === 0) return 0;
  const seen = new Set();
  for (const app of applications) {
    const key = applicationIdentityKey(app || {});
    if (key) seen.add(key);
  }
  return seen.size || applications.length;
}

function toPlanningCount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : null;
}

async function fetchPlanningCouncilCounts() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), COUNCIL_COUNT_TIMEOUT_MS);

  try {
    const url = new URL("/stats/application-counts", getCityScopeBackendBaseUrl());

    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`Planning counts returned ${response.status}`);
    }

    const payload = await response.json();
    const publicCouncils = Object.values(COUNCILS).filter((council) => council.publicEnabled);
    return publicCouncils.reduce((counts, council) => {
      const count = toPlanningCount(payload?.counts?.[council.slug]);
      if (count !== null) counts[council.slug] = count;
      return counts;
    }, { ...FALLBACK_COUNCIL_APPLICATION_COUNTS });
  } finally {
    clearTimeout(timeoutId);
  }
}

function getStructuredDataCount(scope, councilCounts, fetchedCount) {
  if (scope.aggregate) {
    const total = Object.values(councilCounts || {}).reduce((sum, value) => {
      const count = toPlanningCount(value);
      return count === null ? sum : sum + count;
    }, 0);
    return total || fetchedCount;
  }

  const councilCount = toPlanningCount(councilCounts?.[scope.slug]);
  return councilCount ?? fetchedCount;
}

function normalizeAddressKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function buildAddressIndex(applications) {
  const items = buildTextIndex(applications);
  const seen = new Set();

  return items
    .filter((app) => {
      const key = normalizeAddressKey(app?.address);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, ADDRESS_INDEX_LIMIT);
}

function getPlanningUpdatesPath(councilSlug = AGGREGATE_COUNCIL_SLUG) {
  if (councilSlug === "london") return "/planning-updates/london";
  if (isAggregateCouncilScope(councilSlug)) return "/planning-updates";
  const council = getCouncilConfig(councilSlug) || getCouncilConfig(DEFAULT_COUNCIL_SLUG);
  if (!council) return "/planning-updates";
  return `/planning-updates/${councilSlugToShortCode(council.slug)}`;
}

function getPlanningUpdatesAreaPath(areaId = null, fallbackCouncilSlug = AGGREGATE_COUNCIL_SLUG) {
  const raw = String(areaId || "").trim().toLowerCase();
  const [councilSlug, areaSlug] = raw.split(":");
  const council = getCouncilConfig(councilSlug || fallbackCouncilSlug);
  if (!council || !areaSlug) return getPlanningUpdatesPath(fallbackCouncilSlug);
  return `/planning-updates/${council.shortCode}/${areaSlug}`;
}

function buildPlanningUpdatesStructuredData(applications, itemCountOverride = null, councilSlug = AGGREGATE_COUNCIL_SLUG) {
  const scope = getPlanningUpdatesScope(councilSlug);
  const itemCount = Number.isFinite(Number(itemCountOverride))
    ? Number(itemCountOverride)
    : buildTextIndex(applications).length;
  const pagePath = getPlanningUpdatesPath(scope.slug);
  const pageUrl = toAbsoluteSiteUrl(pagePath);
  const councilName = scope.authorityName;
  const placeName = scope.placeName;
  const planningApplicationsPhrase = scope.aggregate
    ? "Scotland's planning applications"
    : `${placeName} planning applications`;
  const planningUpdatesPageLabel = scope.aggregate
    ? "Planning Updates"
    : `${placeName} planning updates`;
  const planningMapLabel = scope.aggregate
    ? "planning map"
    : `${placeName} planning map`;
  const description = getPlanningSeoDescription({
    placeName,
    aggregate: scope.aggregate,
  });
  const mapPath = scope.aggregate
    ? "/planning-map"
    : `/planning-map/${councilSlugToShortCode(scope.slug)}`;
  const mapUrl = toAbsoluteSiteUrl(mapPath);
  const councilProfile = getScottishCouncilSeoProfile(scope.slug);
  const faqEntries = [
    {
      name: `How do I search ${planningApplicationsPhrase}?`,
      text: `City Scope indexes recent ${planningApplicationsPhrase} so they can be searched by address, postcode, reference, status, category, and date.`,
    },
    {
      name: `Can I search ${planningApplicationsPhrase} by reference or postcode?`,
      text: `Yes. The ${planningUpdatesPageLabel} page supports direct searches for planning references, postcodes, streets, proposal text, and application status.`,
    },
    {
      name: `Can I view ${planningApplicationsPhrase} on a map?`,
      text: `Yes. The ${planningMapLabel} shows recent planning applications by location and links each mapped record back to the application detail page.`,
    },
    {
      name: `Where does the ${placeName} planning application data come from?`,
      text: scope.aggregate
        ? `City Scope links each planning record back to the relevant council source where that source URL is available.`
        : `City Scope links ${placeName} planning records back to ${councilName} where the official source URL is available.`,
    },
  ];

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#collection`,
        url: pageUrl,
        name: `${placeName} Planning Updates | City Scope`,
        description,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        provider: { "@id": `${SITE_URL}/#organization` },
        ...(scope.aggregate || !councilProfile
          ? {}
          : {
              spatialCoverage: councilProfile.places.map((name) => ({ "@type": "Place", name })),
              isBasedOn: {
                "@type": "WebPage",
                name: `${councilName} planning application search`,
                url: getCouncilConfig(scope.slug)?.publicSearchUrl,
              },
            }),
        about: [
          planningApplicationsPhrase,
          ...PLANNING_COVERAGE_LINKS.map((link) => link.label),
          "Planning application search",
          "Address planning search",
          "Postcode planning search",
          "Planning map",
        ],
        numberOfItems: itemCount,
        hasPart: [
          {
            "@type": "Map",
            name: `${placeName} planning map`,
            url: mapUrl,
          },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        url: pageUrl,
        mainEntity: faqEntries.map((entry) => ({
          "@type": "Question",
          name: entry.name,
          acceptedAnswer: {
            "@type": "Answer",
            text: entry.text,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Planning Updates",
            item: pageUrl,
          },
        ],
      },
    ],
  };
}

function PlanningCoverageLinks({ showAllCouncils = false }) {
  return (
    <section className="mx-auto w-full max-w-6xl px-4 pb-8">
      <div className="border-t border-[var(--c-border)] pt-5">
        <p className="pr-kicker mb-3">Coverage</p>
        <p className="max-w-3xl text-sm leading-6 text-[var(--c-text-secondary)]">
          {PLANNING_CURRENT_COVERAGE_COPY}
        </p>
        <nav aria-label="Current planning coverage" className="mt-4 flex flex-wrap gap-1.5">
          {PLANNING_COVERAGE_LINKS.map((link) => (
            <Link key={link.href} href={link.href} className="pr-chip">
              {link.label}
            </Link>
          ))}
        </nav>
        {showAllCouncils ? <ScottishCouncilCoverageDirectory /> : null}
      </div>
    </section>
  );
}

function PlanningArchiveNotice({
  title = "Need older refs or exact address matches?",
  description = "Open the full plain-HTML archive for a larger index of planning references and addresses.",
  href = buildPlanningArchivePath(1),
  label = "Open planning archive",
}) {
  return (
    <div className="mt-5 flex flex-col gap-3 border-l-2 border-[var(--c-text-primary)] py-1 pl-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[var(--c-text-primary)]">{title}</div>
        <div className="mt-1 text-sm leading-relaxed text-[var(--c-text-secondary)]">
          {description}
        </div>
      </div>

      <Link href={href} className="pr-textlink shrink-0">
        {label} →
      </Link>
    </div>
  );
}

function ShortLetsSpotlight({ shortLets = [] }) {
  const lastYear = React.useMemo(() => filterShortLetsPastYear(shortLets), [shortLets]);
  const monthly = React.useMemo(() => buildMonthlyCounts(lastYear, 12), [lastYear]);
  const topDistricts = React.useMemo(
    () => topCounts(lastYear.map((item) => extractPostcodeDistrict(item.address)).filter(Boolean), 4),
    [lastYear]
  );
  const retrospectiveCount = React.useMemo(
    () => lastYear.filter((item) => isRetrospectiveShortLet(item)).length,
    [lastYear]
  );
  const formCoverage = React.useMemo(
    () => lastYear.filter((item) => item.has_application_form).length,
    [lastYear]
  );
  const routeMix = React.useMemo(() => {
    const counts = { FULSTL: 0, CLESTL: 0, CLPSTL: 0 };
    lastYear.forEach((item) => {
      const ref = String(item.reference_no || "").toUpperCase();
      if (ref.endsWith("/FULSTL")) counts.FULSTL += 1;
      else if (ref.endsWith("/CLESTL")) counts.CLESTL += 1;
      else if (ref.endsWith("/CLPSTL")) counts.CLPSTL += 1;
    });
    return counts;
  }, [lastYear]);
  const recent = React.useMemo(() => {
    return [...lastYear]
      .sort((left, right) => new Date(right.received_date || right.created_at || 0) - new Date(left.received_date || left.created_at || 0))
      .slice(0, 4);
  }, [lastYear]);
  const maxMonthly = Math.max(1, ...monthly.map((item) => item.count));

  return (
    <section className="mx-auto w-full max-w-6xl px-4 pb-14">
      <div className="border-t-2 border-[var(--c-text-primary)] pt-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="pr-kicker pr-kicker--accent">Short-let watch</p>
            <h2 className="pr-heading mt-3">
              Edinburgh short-term let planning signals
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--c-text-secondary)]">
              Short lets sit inside the planning feed, but this section gives them their own lens:
              yearly momentum, lawfulness-vs-permission mix, hotspot districts, and direct jumps into
              the full watch report.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link href="/planning-updates?category=short_term_lets" className="pr-textlink">
              Filter updates to short lets
            </Link>
            <Link href="/short-lets-report" className="pr-button">
              Open Short-Let Watch
            </Link>
          </div>
        </div>

        <div className="mt-8 grid divide-y divide-[var(--c-border)] border-y border-[var(--c-border)] sm:grid-cols-2 sm:divide-y-0 sm:divide-x xl:grid-cols-4">
          {[
            { label: 'Past year', value: lastYear.length, note: 'Short-let related cases in the last 12 months.' },
            { label: 'Retrospective', value: retrospectiveCount, note: 'Cases explicitly framed as retrospective use changes.' },
            {
              label: 'Forms captured',
              value: `${lastYear.length ? Math.round((formCoverage / lastYear.length) * 100) : 0}%`,
              note: `${formCoverage} records currently expose an application form.`,
            },
            {
              label: 'Top district',
              value: topDistricts[0]?.label || 'N/A',
              note: `${topDistricts[0]?.count || 0} cases in the strongest postcode district.`,
            },
          ].map((stat) => (
            <div key={stat.label} className="px-0 py-4 sm:px-5 sm:first:pl-0">
              <div className="pr-kicker">{stat.label}</div>
              <div className="mt-2 font-['Newsreader',Georgia,serif] text-3xl font-medium text-[var(--c-text-primary)]">
                {stat.value}
              </div>
              <div className="mt-1.5 text-[13px] leading-relaxed text-[var(--c-text-secondary)]">{stat.note}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 grid gap-10 xl:grid-cols-[1.4fr,0.9fr]">
          <div>
            <div className="pr-section-label">12-month momentum</div>
            <div className="mt-6 grid grid-cols-12 items-end gap-2">
              {monthly.map((item) => (
                <div key={item.key} className="flex flex-col items-center gap-2">
                  <div className="flex h-36 w-full items-end justify-center border-b border-[var(--c-border)]">
                    <div
                      className="pr-bar w-full max-w-[26px]"
                      style={{ height: `${Math.max(4, (item.count / maxMonthly) * 100)}%` }}
                      title={`${item.label}: ${item.count}`}
                    />
                  </div>
                  <div className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">{item.label}</div>
                  <div className="font-['DM_Mono'] text-xs tabular-nums text-[var(--c-text-primary)]">{item.count}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid content-start gap-10">
            <div>
              <div className="pr-section-label">Route mix</div>
              <div className="mt-4">
                {[
                  { label: 'Planning Permission', key: 'FULSTL' },
                  { label: 'Existing Lawfulness', key: 'CLESTL' },
                  { label: 'Proposed Lawfulness', key: 'CLPSTL' },
                ].map((item) => (
                  <div key={item.key} className="border-b border-[var(--c-border)] py-3 first:pt-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="text-sm font-medium text-[var(--c-text-primary)]">{item.label}</div>
                      <div className="font-['DM_Mono'] text-sm tabular-nums text-[var(--c-text-primary)]">
                        {routeMix[item.key]}
                      </div>
                    </div>
                    <div className="mt-2 h-[3px] bg-[var(--c-bg-secondary)]">
                      <div
                        className="pr-bar h-full"
                        style={{ width: `${lastYear.length ? (routeMix[item.key] / lastYear.length) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="pr-section-label">Hot districts</div>
              <div className="mt-4">
                {topDistricts.map((district) => (
                  <div key={district.label} className="border-b border-[var(--c-border)] py-3 first:pt-0">
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="font-medium text-[var(--c-text-primary)]">{district.label}</span>
                      <span className="font-['DM_Mono'] tabular-nums text-[var(--c-text-primary)]">{district.count}</span>
                    </div>
                    <div className="mt-2 h-[3px] bg-[var(--c-bg-secondary)]">
                      <div
                        className="pr-bar h-full"
                        style={{ width: `${topDistricts[0] ? (district.count / topDistricts[0].count) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {recent.length > 0 ? (
          <div className="mt-10">
            <div className="pr-section-label">Recent short-let applications</div>
            <div className="mt-4 grid gap-px border border-[var(--c-border)] bg-[var(--c-border)] sm:grid-cols-2 xl:grid-cols-4">
              {recent.map((item) => (
                <Link
                  key={item.reference_no}
                  href={buildPlanningApplicationPath(item.reference_no, item.source_council || DEFAULT_COUNCIL_SLUG)}
                  className="group bg-[var(--c-surface)] p-4 transition-colors hover:bg-[var(--c-surface-hover)]"
                >
                  <div className="line-clamp-2 font-['Newsreader',Georgia,serif] text-base font-medium leading-snug text-[var(--c-text-primary)] group-hover:underline">
                    {item.address || item.description || 'Short-let application'}
                  </div>
                  <div className="mt-2 line-clamp-3 text-[13px] leading-relaxed text-[var(--c-text-secondary)]">
                    {item.ai_summary || item.description || 'Open the planning updates page for the full application record.'}
                  </div>
                  <div className="mt-4 flex items-center justify-between font-['DM_Mono'] text-[11px] uppercase tracking-[0.05em] text-[var(--c-text-tertiary)]">
                    <span>{item.received_date ? toDateLabel(item.received_date) : 'Recent'}</span>
                    <span>{item.status || 'Open'}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

const API_FIELD_PREVIEW_LIMIT = 18;

const DIGEST_FIELD_LABELS = {
  reference_no: "Reference",
  received_date: "Received",
  validated_date: "Validated",
  scraped_date: "Last Scraped",
  created_at: "Record Created",
  scale_rating: "Impact Scale",
  agent_name: "Agent",
  applicant_name: "Applicant",
  decision_date: "Decision Date",
  decision_status: "Decision Status",
  decision_level: "Decision Level",
  committee_date: "Committee Date",
  application_type: "Application Type",
  development_type: "Development Type",
  proposal_type: "Proposal Type",
  site_area: "Site Area",
  ward_name: "Ward",
  council_name: "Council",
  postcode: "Postcode",
  form_pdf_url: "Application Form PDF",
};

const DIGEST_OMIT_FIELDS = new Set([
  "id",
  "ref",
  "reference_no",
  "reference",
  "application_ref",
  "address",
  "description",
  "ai_summary",
  "proposal",
  "details",
  "status",
  "category",
  "documents",
  "link",
  "record_type",
  "received_date",
  "validated_date",
  "scraped_date",
  "created_at",
  "scale_rating",
  "group_id",
  "latitude",
  "longitude",
  "lat",
  "lng",
  "location",
  "coordinates",
  "image_url",
  "thumbnail_url",
  "images",
  "photos",
  "ai_match_data",
]);

function trimDigestText(value, maxLength = 280) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 3)).trim()}...`;
}

function formatDigestFieldLabel(key) {
  if (!key) return "";
  if (DIGEST_FIELD_LABELS[key]) return DIGEST_FIELD_LABELS[key];
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatDigestScalar(key, value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (key === "scale_rating") {
      const normalized = Number.isInteger(value) ? value : value.toFixed(1);
      return `${normalized}/10`;
    }
    return value.toLocaleString("en-GB");
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    if (value.every((item) => item == null || ["string", "number", "boolean"].includes(typeof item))) {
      return value.map((item) => formatDigestScalar(key, item)).filter(Boolean).join(", ");
    }
    return "";
  }
  if (typeof value === "object") return "";

  const text = String(value).trim();
  if (!text) return "";
  if (/(?:_date|_at)$/i.test(key)) {
    return toDateLabel(text) || text;
  }
  return trimDigestText(text, 220);
}

function getDocumentCount(application) {
  return Array.isArray(application?.documents) ? application.documents.filter(Boolean).length : 0;
}

function getLeadDigest(application) {
  return trimDigestText(
    application?.ai_summary || application?.description || application?.proposal || application?.details || "",
    360
  );
}

function getOfficialDescription(application) {
  const description = trimDigestText(application?.description || application?.proposal || application?.details || "", 340);
  const aiSummary = trimDigestText(application?.ai_summary || "", 340);
  if (!description) return "";
  if (aiSummary && description === aiSummary) return "";
  return description;
}

function getDigestFacts(application) {
  const facts = [
    ["Status", application?.status],
    ["Category", application?.category],
    ["Impact Scale", formatDigestScalar("scale_rating", application?.scale_rating)],
    ["Received", toDateLabel(application?.received_date)],
    ["Validated", toDateLabel(application?.validated_date)],
    ["Last Scraped", toDateLabel(application?.scraped_date)],
    ["Applicant", application?.applicant_name],
    ["Agent", application?.agent_name],
    ["Ward", application?.ward || application?.ward_name],
    ["Decision", application?.decision || application?.decision_status],
  ];

  const documentCount = getDocumentCount(application);
  if (documentCount > 0) {
    facts.push(["Documents", `${documentCount} linked file${documentCount === 1 ? "" : "s"}`]);
  }

  return facts.filter(([, value]) => Boolean(value));
}

function getAdditionalApiFields(application) {
  return Object.entries(application || {})
    .map(([key, value]) => [key, formatDigestScalar(key, value)])
    .filter(([key, value]) => !DIGEST_OMIT_FIELDS.has(key) && Boolean(value));
}

function PlanningUpdatesTextIndex({ applications, councilSlug = DEFAULT_COUNCIL_SLUG }) {
  const archiveScope = getPlanningUpdatesScope(councilSlug);
  const indexItems = React.useMemo(
    () => buildTextIndex(applications),
    [applications]
  );

  if (indexItems.length === 0) return null;

  return (
    <section className="mx-auto w-full max-w-6xl px-4 pb-16 pt-10">
      <div className="border-t-2 border-[var(--c-text-primary)] pt-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="pr-kicker pr-kicker--accent">Planning digest</p>
            <h2 className="pr-heading mt-3">
              A readable HTML edition of the live planning feed
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--c-text-secondary)]">
              This is the server-rendered layer beneath the interactive hub. It reads like a structured
              planning bulletin: summaries, key dates, source links, and extra API fields in plain HTML.
            </p>
          </div>
          <div className="pr-data shrink-0">
            {indexItems.length} records rendered
          </div>
        </div>

        <div>
          <PlanningArchiveNotice
            title={!archiveScope.council ? "Need the full crawlable archive?" : `Every ${archiveScope.placeName} planning application`}
            description={
              !archiveScope.council
                ? "The archive stays useful for deeper address and reference coverage, while this digest renders the server-fetched feed in a richer format."
                : `This digest shows the latest ${archiveScope.placeName} applications. The archive lists every ${archiveScope.placeName} application we hold, by reference and address, in plain HTML.`
            }
            href={archiveScope.council ? buildPlanningArchivePath(1, archiveScope.council.slug) : buildPlanningArchivePath(1)}
            label={archiveScope.council ? `Browse the ${archiveScope.placeName} archive` : "Open planning archive"}
          />

          <ol className="mt-8">
            {indexItems.map((app) => {
              const ref = String(app.reference_no || "").trim();
              const address = String(app.address || "").trim();
              const leadSummary = getLeadDigest(app);
              const officialDescription = getOfficialDescription(app);
              const status = String(app.status || "").trim();
              const category = String(app.category || "").trim();
              const received = toDateLabel(app.received_date);
              const validated = toDateLabel(app.validated_date);
              const scraped = toDateLabel(app.scraped_date);
              const applicationHref = buildPlanningApplicationPath(
                ref,
                app.source_council || councilSlug
              );
              const externalLink = app.link ? String(app.link) : null;
              const digestFacts = getDigestFacts(app);
              const additionalFields = getAdditionalApiFields(app);

              return (
                <li key={applicationIdentityKey(app || {}) || ref} className="pr-ledger-row">
                  <article className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(300px,0.9fr)]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-['DM_Mono'] text-[11px] uppercase tracking-[0.08em] text-[var(--c-text-tertiary)]">
                        {ref ? (
                          <span className="text-[var(--c-text-secondary)]">
                            Ref {getCouncilQualifiedApplicationReference(app, councilSlug)}
                          </span>
                        ) : null}
                        {status ? <span>{status}</span> : null}
                        {category ? <span>{category}</span> : null}
                        {app.record_type === "short_term_let" ? (
                          <span className="text-[var(--c-accent)]">Short-Let Watch</span>
                        ) : null}
                      </div>

                      <h3 className="mt-2 font-['Newsreader',Georgia,serif] text-xl font-medium leading-snug text-[var(--c-text-primary)]">
                        {address || "Planning application"}
                      </h3>

                      {(received || validated || scraped) ? (
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-['DM_Mono'] text-[11px] text-[var(--c-text-tertiary)]">
                          {received ? <span>Received {received}</span> : null}
                          {validated ? <span>Validated {validated}</span> : null}
                          {scraped ? <span>Updated {scraped}</span> : null}
                        </div>
                      ) : null}

                      {leadSummary ? (
                        <p className="mt-4 text-sm leading-7 text-[var(--c-text-primary)]">
                          {leadSummary}
                        </p>
                      ) : null}

                      {officialDescription ? (
                        <p className="mt-3 border-l-2 border-[var(--c-border)] pl-3 text-sm leading-7 text-[var(--c-text-secondary)]">
                          {officialDescription}
                        </p>
                      ) : null}

                      <div className="mt-4 flex flex-wrap gap-4" data-nosnippet>
                        <Link
                          href={applicationHref}
                          prefetch={false}
                          className="pr-textlink"
                          title="Open the dedicated planning application page"
                        >
                          View application detail →
                        </Link>
                        {externalLink ? (
                          <a
                            href={externalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pr-textlink"
                          >
                            Council source ↗
                          </a>
                        ) : null}
                      </div>

                      {additionalFields.length > 0 ? (
                        <details className="mt-4">
                          <summary className="cursor-pointer font-['DM_Mono'] text-[11px] uppercase tracking-[0.08em] text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]">
                            More API fields ({additionalFields.length})
                          </summary>
                          <dl className="mt-3 grid gap-x-5 sm:grid-cols-2">
                            {additionalFields.slice(0, API_FIELD_PREVIEW_LIMIT).map(([key, value]) => (
                              <div key={`${ref}-${key}`} className="pr-fact">
                                <dt>{formatDigestFieldLabel(key)}</dt>
                                <dd>{value}</dd>
                              </div>
                            ))}
                          </dl>
                          {additionalFields.length > API_FIELD_PREVIEW_LIMIT ? (
                            <div className="mt-3 text-xs text-[var(--c-text-secondary)]">
                              {additionalFields.length - API_FIELD_PREVIEW_LIMIT} more scalar fields are present in the record and can be exposed if needed.
                            </div>
                          ) : null}
                        </details>
                      ) : null}
                    </div>

                    <div className="min-w-0">
                      <dl>
                        {digestFacts.map(([label, value]) => (
                          <div key={`${ref}-${label}`} className="pr-fact">
                            <dt>{label}</dt>
                            <dd>{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}

function PlanningUpdatesAddressIndex({ applications, councilSlug = DEFAULT_COUNCIL_SLUG }) {
  const addressItems = React.useMemo(
    () => buildAddressIndex(applications),
    [applications]
  );

  if (addressItems.length === 0) return null;

  return (
    <section className="mx-auto w-full max-w-6xl px-4 pb-16">
      <div className="border-t border-[var(--c-border)] pt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="pr-kicker">Street &amp; application shelf</p>
            <p className="mt-2 text-sm leading-relaxed text-[var(--c-text-secondary)]">
              A lighter HTML shelf for direct address and postcode lookups underneath the richer digest above.
            </p>
          </div>
          <div className="pr-data shrink-0">
            {addressItems.length} recent addresses
          </div>
        </div>

        <ol className="mt-6 grid gap-x-8 sm:grid-cols-2 xl:grid-cols-3">
          {addressItems.map((app) => {
            const ref = String(app.reference_no || "").trim();
            const address = String(app.address || "").trim();
            const status = String(app.status || "").trim();
            const href = buildPlanningApplicationPath(ref, app.source_council || councilSlug);
            const received = toDateLabel(app.received_date);

            return (
              <li
                key={`address-${applicationIdentityKey(app || {}) || ref}`}
                className="pr-address-index-row border-b border-dotted border-[var(--c-border)] py-2.5"
              >
                <Link
                  href={href}
                  prefetch={false}
                  className="text-sm font-medium leading-6 text-[var(--c-text-primary)] hover:text-[var(--c-accent)] hover:underline"
                >
                  {address || "Planning application detail"}
                </Link>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-['DM_Mono'] text-[11px] text-[var(--c-text-tertiary)]">
                  {status ? <span>{status}</span> : null}
                  {received ? <span>{received}</span> : null}
                  {app.record_type === 'short_term_let' ? (
                    <span className="text-[var(--c-accent)]">Short-Let</span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

const PlanningUpdates = ({
  initialApplications = [],
  initialShortLets = [],
  initialReferenceDate = null,
  initialApplicationCount = null,
  initialLoadUnavailable = false,
  councilSlug = AGGREGATE_COUNCIL_SLUG,
  areaId = null,
}) => {
  const scope = getPlanningUpdatesScope(councilSlug);
  const [clientScopeSlug, setClientScopeSlug] = React.useState(scope.slug);
  const [clientAreaId, setClientAreaId] = React.useState(areaId || null);

  React.useEffect(() => {
    setClientScopeSlug(scope.slug);
  }, [scope.slug]);
  React.useEffect(() => {
    setClientAreaId(areaId || null);
  }, [areaId]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncScopeFromLocation = (event) => {
      const eventCouncil = event instanceof CustomEvent ? event.detail?.council : null;
      if (typeof eventCouncil === "string" && eventCouncil) {
        setClientScopeSlug(getPlanningUpdatesScope(eventCouncil).slug);
        setClientAreaId(event instanceof CustomEvent ? event.detail?.areaId || null : null);
        return;
      }

      const eventHref = event instanceof CustomEvent ? event.detail?.href : null;
      const path = typeof eventHref === "string" && eventHref
        ? eventHref
        : window.location.pathname;
      setClientScopeSlug((current) => getPlanningUpdatesScopeSlugFromPath(path, current));
      setClientAreaId(getPlanningUpdatesAreaIdFromPath(path));
    };

    window.addEventListener(PLANNING_REGION_ROUTE_EVENT, syncScopeFromLocation);
    window.addEventListener("popstate", syncScopeFromLocation);

    return () => {
      window.removeEventListener(PLANNING_REGION_ROUTE_EVENT, syncScopeFromLocation);
      window.removeEventListener("popstate", syncScopeFromLocation);
    };
  }, []);

  const currentScope = React.useMemo(
    () => getPlanningUpdatesScope(clientScopeSlug),
    [clientScopeSlug]
  );
  const scopedInitialApplications = React.useMemo(
    () => {
      const sourceCouncil = getCouncilConfig(scope.slug);
      const sourceIsHiddenCouncil = !scope.aggregate && sourceCouncil?.publicEnabled === false;
      if (currentScope.aggregate && sourceIsHiddenCouncil) {
        return filterPlanningApplicationsForScope(initialApplications, currentScope)
          .filter((application) => getCouncilConfigFromApplication(application)?.publicEnabled === true);
      }
      return filterPlanningApplicationsForScope(initialApplications, currentScope);
    },
    [currentScope, initialApplications, scope.aggregate, scope.slug]
  );
  const activeInitialApplications = React.useMemo(
    () => (clientAreaId ? [] : scopedInitialApplications),
    [clientAreaId, scopedInitialApplications]
  );
  const interactiveInitialApplications = React.useMemo(
    () => activeInitialApplications.slice(0, INTERACTIVE_FEED_PAGE_SIZE),
    [activeInitialApplications]
  );
  const activeApplicationCount = currentScope.slug === scope.slug
    ? initialApplicationCount
    : FALLBACK_COUNCIL_APPLICATION_COUNTS[currentScope.slug] ?? activeInitialApplications.length;
  const placeName = currentScope.placeName;
  const pagePath = clientAreaId
    ? getPlanningUpdatesAreaPath(clientAreaId, currentScope.slug)
    : getPlanningUpdatesPath(currentScope.slug);
  const structuredData = React.useMemo(
    () => buildPlanningUpdatesStructuredData(activeInitialApplications, activeApplicationCount, currentScope.slug),
    [activeApplicationCount, activeInitialApplications, currentScope.slug]
  );

  return (
    <>
      <Meta
        title={currentScope.aggregate
          ? "Scotland's Planning Applications Search | City Scope"
          : `View ${placeName} Planning Applications | City Scope`
        }
        description={getPlanningSeoDescription({
          placeName,
          aggregate: currentScope.aggregate,
        })}
        canonical={pagePath}
        noindex={initialLoadUnavailable || Boolean(currentScope.council && !currentScope.council.publicEnabled)}
        nofollow={Boolean(currentScope.council && !currentScope.council.publicEnabled)}
        structuredData={structuredData}
      />
      <div className="planning-register min-h-screen bg-[var(--c-bg)]">
        <PlanningContextProvider
          initialApplications={interactiveInitialApplications}
          initialReferenceDate={initialReferenceDate}
          councilSlug={currentScope.slug}
          areaId={clientAreaId}
          disableDataBootstrap
          suppressLegacyShortLetHydration
        >
          <PlanningSearchProvider>
            <PlanningDesignSwitcher />
          </PlanningSearchProvider>
        </PlanningContextProvider>

        {!currentScope.aggregate ? (
          <CouncilSearchGuide
            councilSlug={currentScope.slug}
            applications={activeInitialApplications}
            applicationCount={activeApplicationCount}
            referenceDate={initialReferenceDate}
          />
        ) : null}
        {initialShortLets.length > 0 ? <ShortLetsSpotlight shortLets={initialShortLets} /> : null}
        {currentScope.aggregate ? <PlanningCoverageLinks showAllCouncils /> : null}
        <PlanningUpdatesTextIndex applications={activeInitialApplications} councilSlug={currentScope.slug} />
        <PlanningUpdatesAddressIndex applications={activeInitialApplications} councilSlug={currentScope.slug} />
      </div>
    </>
  );
};

export function PlanningUpdatesHtmlDocument({
  initialApplications = [],
  initialShortLets = [],
  initialApplicationCount = null,
  councilSlug = AGGREGATE_COUNCIL_SLUG,
}) {
  const scope = getPlanningUpdatesScope(councilSlug);
  const placeName = scope.placeName;
  const pagePath = getPlanningUpdatesPath(scope.slug);
  const structuredData = React.useMemo(
    () => buildPlanningUpdatesStructuredData(initialApplications, initialApplicationCount, scope.slug),
    [initialApplications, initialApplicationCount, scope.slug]
  );

  return (
    <>
      <Meta
        title="Planning Applications HTML View | City Scope"
        description={`Plain server-rendered HTML view of recent ${placeName} planning applications for crawlability checks and non-JavaScript readers.`}
        canonical={pagePath}
        noindex
        structuredData={structuredData}
      />
      <main className="planning-register min-h-screen bg-[var(--c-bg)]">
        <section className="mx-auto w-full max-w-6xl px-4 py-10">
          <div className="border-t-2 border-[var(--c-text-primary)] pt-6">
            <p className="pr-kicker">HTML mode</p>
            <h1 className="pr-heading mt-3 sm:text-3xl">
              Crawlable planning updates
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-[var(--c-text-secondary)] sm:text-base">
              This page intentionally skips the interactive hub and renders the same server-fetched planning
              records as plain HTML. Use it to inspect the content a crawler or non-JavaScript reader can read
              after a normal document request.
            </p>
          </div>
        </section>

        {initialShortLets.length > 0 ? <ShortLetsSpotlight shortLets={initialShortLets} /> : null}
        <PlanningUpdatesTextIndex applications={initialApplications} councilSlug={scope.slug} />
        <PlanningUpdatesAddressIndex applications={initialApplications} councilSlug={scope.slug} />
      </main>
    </>
  );
}

export async function getPlanningUpdatesStaticProps({
  includeApplications = true,
  initialFetchLimit = INITIAL_PAGE_FETCH_LIMIT,
  pageSize = INITIAL_PAGE_FETCH_PAGE_SIZE,
  maxPages = INITIAL_PAGE_FETCH_MAX_PAGES,
  councilSlug = AGGREGATE_COUNCIL_SLUG,
  preferLightFeed = false,
  areaId = null,
} = {}) {
  const initialReferenceDate = new Date().toISOString();
  const scope = getPlanningUpdatesScope(councilSlug);

  if (!includeApplications) {
    return {
      props: {
        initialApplications: [],
        initialShortLets: [],
        initialReferenceDate,
        initialApplicationCount: null,
        councilSlug: scope.slug,
        areaId: areaId || null,
      },
      revalidate: 600,
    };
  }

  if (areaId) {
    return {
      props: {
        initialApplications: [],
        initialShortLets: [],
        initialReferenceDate,
        initialApplicationCount: null,
        councilSlug: scope.slug,
        areaId,
      },
      revalidate: 120,
    };
  }

  // London uses one bounded source page and its own cursor. Never load the
  // full regional dataset or infer a source total from the SSR slice.
  if (isLondonScope(scope.slug)) {
    try {
      const page = await fetchPlanningFeedLightPage({
        council: scope.slug, limit: Math.min(initialFetchLimit, 100), retries: 0,
      });
      return { props: {
        initialApplications: page.applications, initialShortLets: [], initialReferenceDate,
        initialApplicationCount: page.total, councilSlug: scope.slug, areaId: null,
      }, revalidate: 600 };
    } catch (error) {
      console.warn('London planning updates source page unavailable');
      return { props: {
        initialApplications: [], initialShortLets: [], initialReferenceDate,
        initialApplicationCount: null, initialLoadUnavailable: true,
        councilSlug: scope.slug, areaId: null,
      }, revalidate: 120 };
    }
  }

  let fullData = [];

  if (!scope.aggregate && !preferLightFeed) {
    try {
      fullData = await fetchAllUnifiedPlanningFeed({
        // A Vercel build must not be held hostage by a live API burst. The
        // client hydrates the same page after load and ISR refreshes it later,
        // so keep one small, bounded crawlable slice for the build artifact.
        pageSize: IS_PRODUCTION_BUILD ? Math.min(pageSize, 50) : pageSize,
        maxPages: IS_PRODUCTION_BUILD ? 1 : maxPages,
        pageConcurrency: 1,
        retries: 0,
        timeoutMs: IS_PRODUCTION_BUILD ? PRODUCTION_BUILD_FEED_TIMEOUT_MS : 8000,
        headers: { "X-CityScope-Interactive": "ssr-feed" },
        includeShortLets: false,
        council: scope.apiCouncil,
      });
    } catch (error) {
      console.error("planning-updates rich getStaticProps feed fetch failed", error);
    }
  }

  if (!Array.isArray(fullData) || fullData.length === 0) {
    try {
      const boundedPageSize = IS_PRODUCTION_BUILD ? Math.min(pageSize, 50) : pageSize;
      const lightFeedOptions = {
        retries: 0,
        timeoutMs: IS_PRODUCTION_BUILD ? PRODUCTION_BUILD_FEED_TIMEOUT_MS : 8000,
        headers: { "X-CityScope-Interactive": "ssr-feed" },
        council: scope.apiCouncil,
      };
      // Cold council hubs and the aggregate page only need one crawlable slice.
      // Avoid the multi-size recovery sequence here: a total origin outage would
      // otherwise spend four timeout windows and overrun the Vercel function.
      fullData = maxPages === 1
        ? await fetchApplicationsLight({
            ...lightFeedOptions,
            limit: boundedPageSize,
            offset: 0,
          })
        : await fetchAllApplicationsLight({
            ...lightFeedOptions,
            pageSize: boundedPageSize,
            maxPages: IS_PRODUCTION_BUILD ? 1 : maxPages,
            pageConcurrency: 1,
          });
    } catch (error) {
      console.error("planning-updates light getStaticProps feed fetch failed", error);
      return {
        props: {
          initialApplications: [],
          initialShortLets: [],
          initialReferenceDate,
          initialApplicationCount: null,
          initialLoadUnavailable: true,
          councilSlug: scope.slug,
          areaId: null,
        },
        revalidate: 120,
      };
    }
  }

  if (!Array.isArray(fullData) || fullData.length === 0) {
    return {
      props: {
        initialApplications: [],
        initialShortLets: [],
        initialReferenceDate,
        initialApplicationCount: null,
        councilSlug: scope.slug,
        areaId: null,
      },
      revalidate: 120,
    };
  }

  const shortLets = [];

  // Keep the statically rendered page bounded while still exposing the full
  // server-fetched slice to crawlers and non-JavaScript readers.
  const scopedFullData = filterPlanningApplicationsForScope(fullData, scope);
  const data = JSON.parse(
    JSON.stringify(Array.isArray(scopedFullData) ? scopedFullData.slice(0, initialFetchLimit) : [])
  );
  const uniqueApplicationCount = countUniqueApplications(scopedFullData);
  let councilCounts = { ...FALLBACK_COUNCIL_APPLICATION_COUNTS };
  if (FETCH_STATIC_COUNCIL_COUNTS) {
    try {
      councilCounts = await fetchPlanningCouncilCounts();
    } catch (error) {
      console.warn("planning-updates count fetch failed", error);
    }
  }
  const structuredDataCount = getStructuredDataCount(scope, councilCounts, uniqueApplicationCount || data.length);

  return {
    props: {
      initialApplications: data,
      initialShortLets: shortLets,
      initialReferenceDate,
      initialApplicationCount: structuredDataCount,
      councilSlug: scope.slug,
      areaId: null,
    },
    // Revalidate at most every 10 minutes to keep data fresh without rebuilding constantly
    revalidate: 600,
  };
}

export async function getStaticProps() {
  return getPlanningUpdatesStaticProps({
    includeApplications: true,
    initialFetchLimit: INTERACTIVE_INITIAL_PAGE_FETCH_LIMIT,
    pageSize: INTERACTIVE_INITIAL_PAGE_FETCH_PAGE_SIZE,
    maxPages: INTERACTIVE_INITIAL_PAGE_FETCH_MAX_PAGES,
  });
}

export default PlanningUpdates;
